/**
 * Shared logic for "identification number" input fields across the director /
 * auditor sheets (roles 4, 5, 6, 7).
 *
 * ## What this decides
 * An `*IdentificationNumber` control is restricted to **numeric keystrokes only**
 * (via PrimeNG's `pKeyFilter` / a digit regex) **when, and only when**, its paired
 * `*TypeOfIdentification` value is one of the Malaysian numeric ID types
 * (MyKad / MyPR / MyKAS — all 12-digit). Every other type (passports, foreign IDs,
 * government-agency IDs, …) is a **free-text** input.
 *
 * ## Why it lives here
 * This predicate used to be copy-pasted into each component's `shouldBeNumericOnly()`,
 * where a stray leading `return true` made *every* identification field numeric
 * regardless of the selected type (the dead-code branch below it never ran). Each
 * sheet also drifted slightly (inline list vs per-role constant). Centralising the
 * predicate here means the four sheets share one tested implementation and cannot
 * re-introduce that bug independently.
 *
 * NOTE: keep {@link NUMERIC_IDENTIFICATION_TYPES} in sync with each role's
 * `ROLE*_IDENTIFICATION_TYPES_REQUIRING_PATTERN` / `_REQUIRING_NUMERIC` validator
 * constant — they describe the same MyKad/MyPR/MyKAS set, from the *validation* side.
 */

/** The identification types whose number is a 12-digit numeric ID. */
export const NUMERIC_IDENTIFICATION_TYPES: ReadonlyArray<string> = ['MyKad', 'MyPR', 'MyKAS'];

/** Length cap for a numeric (MyKad/MyPR/MyKAS) ID number — all exactly 12 digits. */
export const NUMERIC_IDENTIFICATION_MAX_LENGTH = 12;

/**
 * Length cap for a *free-text* identification number (any non-numeric type, e.g.
 * passport / foreign / government-agency IDs). Matches the standard text-field
 * limit (`Validators.maxLength(255)`) used across these sheets, so a free-flow
 * value is not artificially restricted to the 12-digit numeric-ID limit.
 */
export const FREE_TEXT_IDENTIFICATION_MAX_LENGTH = 255;

/** Substring that marks an identification *number* control's qname. */
const IDENTIFICATION_NUMBER_TOKEN = 'IdentificationNumber';
/** Substring of the paired *type* control's qname. */
const IDENTIFICATION_TYPE_TOKEN = 'TypeOfIdentification';

/**
 * Derive the paired `*TypeOfIdentification` qname from an `*IdentificationNumber`
 * qname (e.g. `…DirectorIdentificationNumber` → `…DirectorTypeOfIdentification`).
 * Returns the input unchanged if it is not an identification-number field.
 */
export function identificationTypeFieldFor(fieldName: string): string {
  return fieldName.replace(IDENTIFICATION_NUMBER_TOKEN, IDENTIFICATION_TYPE_TOKEN);
}

/**
 * `true` only for an identification-number field whose selected type is a numeric
 * (MyKad/MyPR/MyKAS) ID. Drives the PrimeNG `pKeyFilter` / `getKeyFilter()` masks.
 *
 * @param fieldName   The control's qname.
 * @param typeValue   The current value of the paired `*TypeOfIdentification` control.
 * @param numericTypes Override list (defaults to {@link NUMERIC_IDENTIFICATION_TYPES}).
 */
export function isNumericOnlyIdentificationField(
  fieldName: string,
  typeValue: unknown,
  numericTypes: ReadonlyArray<string> = NUMERIC_IDENTIFICATION_TYPES,
): boolean {
  if (!fieldName.includes(IDENTIFICATION_NUMBER_TOKEN)) {
    return false;
  }
  if (typeof typeValue !== 'string' || typeValue.length === 0) {
    return false;
  }
  // Case-INsensitive on purpose: conversion sources (LLM / N8N / legacy backend
  // codes) still emit the uppercase `MYKAD` / `MYPR` / `MYKAS` spellings, and a
  // value only reaches the taxonomy casing if it happened to pass through
  // `canonicalizeIdentificationTypes`. Matching exactly would silently downgrade
  // a drifted MyKad to a free-text ID — no numeric filter, no 12-char cap, no
  // 12-digit rule. Mirrors `role5TypeRequiresPattern` / `role4TypeRequiresPattern`.
  const normalized = typeValue.trim().toLowerCase();
  return numericTypes.some((type) => type.toLowerCase() === normalized);
}

/**
 * The `maxlength` cap an `*IdentificationNumber` control should expose, derived
 * from its paired `*TypeOfIdentification` value — the length analogue of
 * {@link isNumericOnlyIdentificationField}:
 *
 * - a numeric type (MyKad/MyPR/MyKAS) → {@link NUMERIC_IDENTIFICATION_MAX_LENGTH} (12);
 * - any other / not-yet-selected type → {@link FREE_TEXT_IDENTIFICATION_MAX_LENGTH} (255);
 * - **not** an identification-number field → `null`, signalling the caller to fall
 *   back to its schema-derived max length (`getFieldMaxLength`).
 *
 * The 12-cap is intentionally conditional (parallel to the conditional 12-digit
 * pattern validator): a free-flow identification number must not be restricted to
 * the numeric-ID limit.
 *
 * @param fieldName    The control's qname.
 * @param typeValue    The current value of the paired `*TypeOfIdentification` control.
 * @param numericTypes Override list (defaults to {@link NUMERIC_IDENTIFICATION_TYPES}).
 */
export function identificationNumberMaxLength(
  fieldName: string,
  typeValue: unknown,
  numericTypes: ReadonlyArray<string> = NUMERIC_IDENTIFICATION_TYPES,
): number | null {
  if (!fieldName.includes(IDENTIFICATION_NUMBER_TOKEN)) {
    return null;
  }
  return isNumericOnlyIdentificationField(fieldName, typeValue, numericTypes)
    ? NUMERIC_IDENTIFICATION_MAX_LENGTH
    : FREE_TEXT_IDENTIFICATION_MAX_LENGTH;
}

/**
 * Snap a single `TypeOfIdentification` value to its canonical option, matching
 * case-insensitively (and trimming surrounding whitespace).
 *
 * Conversion sources (LLM / N8N / legacy backend codes) sometimes emit a value
 * whose casing differs from the taxonomy enum — e.g. `"Police ID Number"` vs the
 * canonical `"Police ID number"`, or the uppercase `"MYKAD"` vs `"MyKad"`. A PrimeNG
 * `select` only renders a value that **exactly** equals one of its options, so a
 * drifted value silently shows as blank ("not mapped"). This returns the canonical
 * option when one matches case-insensitively, otherwise the **original value
 * unchanged** — an unknown value is never destroyed (it just stays unmapped, as before).
 *
 * @param value          The incoming value (any type; non-strings pass through).
 * @param canonicalTypes The sheet's `TypeOfIdentification` enum (from `types.dict.json`).
 */
export function canonicalizeIdentificationType(
  value: unknown,
  canonicalTypes: ReadonlyArray<string>,
): unknown {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  const needle = value.trim().toLowerCase();
  const match = canonicalTypes.find((opt) => opt.toLowerCase() === needle);
  return match ?? value;
}

/**
 * Return a shallow copy of a sheet-data slice with every `*TypeOfIdentification*`
 * value canonicalized via {@link canonicalizeIdentificationType}. Keys are matched
 * by the same `TypeOfIdentification` token used across this util, so it covers the
 * director/auditor/other-person variants uniformly. Non-type keys are copied as-is.
 *
 * Apply this to backend/persisted data **before** `form.patchValue` so the type
 * dropdowns bind to a valid option. If `canonicalTypes` is empty (enum not yet
 * loaded) it is a no-op — values pass through unchanged.
 */
export function canonicalizeIdentificationTypes<T extends Record<string, any>>(
  data: T,
  canonicalTypes: ReadonlyArray<string>,
): T {
  if (!canonicalTypes || canonicalTypes.length === 0) {
    return data;
  }
  const out: Record<string, any> = { ...data };
  for (const key of Object.keys(out)) {
    if (key.includes(IDENTIFICATION_TYPE_TOKEN)) {
      out[key] = canonicalizeIdentificationType(out[key], canonicalTypes);
    }
  }
  return out as T;
}
